import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'informed';
import { format } from 'date-fns';
import { Button } from '@unbooking/ui-modules';
import { IDropdownOption } from '@ui-modules/types';
import './styles.scss';

import { useFacility, useVehicles } from '@common/hooks';
import { IDriverDetails, IDriverDetailsApi } from '@common/interfaces';
import { DriverPhohoPlaceholder } from '@assets/svg/icons';
import {
  CityField,
  CountryField,
  DriverBloodGroupField,
  DriverExpirationDateField,
  DriverLicenseNumberField,
  DriverSharedAgencyField,
  EmailField,
  FirstNameField,
  LastNameField,
  PhoneNumberField,
  PreferredVehicleField,
} from '../fields';

export interface FormDriverEdit {
  bloodGroup: IDropdownOption;
  email: string;
  lastModifiedDate: string;
  lastName: string;
  licenseExpirationDate: Date;
  licenseNumber: string;
  name: string;
  phoneNumber: string;
  photo: string;
  preferredVehicle: IDropdownOption;
  sharedResources: IDropdownOption[];
}

export interface IDriverEditForm {
  driver: IDriverDetails;
  readOnly?: boolean;
  onSubmit: ({ driverId, data }: { driverId: string; data: Partial<IDriverDetailsApi> }) => void;
}

const DriverEditForm: FC<IDriverEditForm> = ({ driver, readOnly = false, onSubmit }) => {
  const {
    facility: { city, country, sharedWithAgencies },
  } = useFacility();
  const { t } = useTranslation();

  const { vehicleOptions } = useVehicles();

  const {
    bloodGroup,
    bloodGroupDisplay,
    email,
    lastModifiedDate,
    lastName,
    licenseExpirationDate,
    licenseNumber,
    name,
    phoneNumber,
    photo,
    preferredVehicle,
    sharedResources,
  } = driver;

  return (
    <Form
      className="form-driver-edit modal-light-form"
      onSubmit={(formState: Record<string, unknown>) => {
        const state = { ...(formState.values as FormDriverEdit) };

        onSubmit({
          driverId: driver.id,
          data: {
            email: state.email,
            last_name: state.lastName,
            license_expiration_date: state.licenseExpirationDate
              ? format(state.licenseExpirationDate, 'yyyy-MM-dd')
              : '',
            license_number: state.licenseNumber,
            name: state.name,
            phone_number: state.phoneNumber,
            ...(state.bloodGroup?.value ? { blood_group: Number(state.bloodGroup.value) } : {}),
            ...(state.preferredVehicle?.value ? { preferred_vehicle: state.preferredVehicle } : {}),
            ...(state.sharedResources?.length
              ? {
                  shared_resources: state.sharedResources,
                }
              : {}),
          },
        });
      }}
    >
      <h2>{t('mobility.editDriver')}</h2>
      <div className="row driver">
        <div className="driver-photo">
          {photo ? (
            <img src={photo} alt="" />
          ) : (
            <div className="driver-photo-placeholder">
              <DriverPhohoPlaceholder />
            </div>
          )}
        </div>

        <div className="driver-info">
          <div className="name">
            {name} {lastName}
          </div>
          {sharedResources.length > 0 && (
            <div className="shared">
              {t('mobility.sharedWith')}
              <span className="shared-label">
                {sharedResources.length} {t('common.agencies')}
              </span>
            </div>
          )}
        </div>

        {bloodGroupDisplay && (
          <DriverBloodGroupField defaultValue={bloodGroup} disabled={readOnly} />
        )}

        <DriverSharedAgencyField
          defaultValue={sharedResources}
          disabled={readOnly}
          options={sharedWithAgencies}
        />
        <PreferredVehicleField
          defaultValue={preferredVehicle || undefined}
          disabled={readOnly}
          options={vehicleOptions}
        />
      </div>

      <h3>{t('mobility.personalInfo')}</h3>
      <div className="row">
        <FirstNameField defaultValue={name} disabled={readOnly} />
        <LastNameField defaultValue={lastName} disabled={readOnly} />
        <EmailField defaultValue={email} disabled={readOnly} label={t('common.changeEmail')} />
        <PhoneNumberField
          defaultValue={phoneNumber}
          disabled={readOnly}
          label={t('common.changePhoneNumber')}
        />
      </div>

      <div className="row driver">
        <div className="driver-license">
          <h3>{t('mobility.driverLicense')}</h3>
          <div className="row">
            <DriverLicenseNumberField defaultValue={licenseNumber} disabled={readOnly} />
            <DriverExpirationDateField defaultValue={licenseExpirationDate} disabled={readOnly} />
          </div>
        </div>
        <div className="driver-location">
          <h3>{t('mobility.location')}</h3>
          <div className="row">
            <CountryField defaultValue={country} disabled />
            <CityField defaultValue={city} disabled />
          </div>
        </div>
      </div>

      <div className="footer">
        <div className="last-modified">
          {t('mobility.lastModified')} {lastModifiedDate}
        </div>
        {!readOnly ? (
          <Button
            className="btn btn-submit"
            text={t('common.btnConfirmChanges')}
            type="submit"
            variant="submit"
          />
        ) : null}
      </div>
    </Form>
  );
};

export default DriverEditForm;
